import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Clock, Calendar, Tag } from 'lucide-react';
import { getPostBySlug, getAllPosts } from '../data/blogData';
import SEO from '../components/SEO';
import { trackBlogView } from '../utils/analytics';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (post) trackBlogView(slug, post.title);
  }, [slug, post]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Post Not Found</h1>
          <button onClick={() => navigate('/blog')} className="text-[#A1A1AA] hover:text-white underline">Back to Blog</button>
        </div>
      </div>
    );
  }

  // Get related posts (same category, excluding current)
  const allPosts = getAllPosts();
  const related = allPosts
    .filter(p => p.slug !== slug && p.category === post.category)
    .slice(0, 2);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "datePublished": post.date,
    "author": { "@type": "Organization", "name": "Agentic Atoms", "url": "https://agenticatoms.com" },
    "publisher": { "@type": "Organization", "name": "Agentic Atoms", "url": "https://agenticatoms.com" },
    "keywords": post.keyword,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://agenticatoms.com/blog/${slug}` }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-20" data-testid="blog-post-page">
      <div className="orange-orb orange-orb-1 top-32 -left-32 w-[28rem] h-[28rem] z-0" />
      <div className="orange-orb orange-orb-2 top-1/2 -right-32 w-[32rem] h-[32rem] z-0" />
      <div className="relative z-10">
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={`${post.keyword}, AI chatbot, AI voice agent, AI receptionist, AI sales agent, AI automation, Agentic Atoms`}
        path={`/blog/${slug}`}
        type="article"
        jsonLd={jsonLd}
      />

      <article className="max-w-3xl mx-auto px-6">
        {/* Back Link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors mb-8 text-sm"
          data-testid="blog-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              post.category === 'Industry' ? 'bg-[#FF6B00]/10 text-[#FF6B00]' : 'bg-[#FF6B00]/10 text-[#FF6B00]'
            }`}>
              {post.category}
            </span>
            <span className="flex items-center gap-1 text-xs text-[#71717A]">
              <Tag className="w-3 h-3" /> {post.industry}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-5 leading-tight">
            {post.title}
          </h1>

          <div className="flex items-center gap-5 text-sm text-[#71717A]">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> {post.readTime}
            </span>
          </div>
        </motion.header>

        {/* Hero Stat */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex items-center gap-4 p-6 bg-[#050505] rounded-2xl border border-white/[0.08] mb-10"
          data-testid="blog-hero-stat"
        >
          <div className="text-4xl font-bold text-[#FF6B00]">{post.heroStat.value}</div>
          <div className="text-sm text-[#A1A1AA] leading-relaxed">{post.heroStat.label}</div>
        </motion.div>

        {/* Content Sections */}
        <div className="space-y-10" data-testid="blog-content">
          {post.sections.map((section, index) => (
            <motion.section
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 + index * 0.05 }}
            >
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 leading-snug">
                {section.heading}
              </h2>
              <div className="text-[#A1A1AA] leading-[1.8] text-base space-y-4">
                {section.content.split('\n\n').map((paragraph, pIndex) => {
                  // Handle bold text with **
                  const renderText = (text) => {
                    const parts = text.split(/(\*\*[^*]+\*\*)/g);
                    return parts.map((part, i) => {
                      if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
                      }
                      return part;
                    });
                  };

                  // Detect list items
                  if (paragraph.match(/^\d+\.\s/m) || paragraph.startsWith('- ')) {
                    const items = paragraph.split('\n').filter(line => line.trim());
                    return (
                      <div key={pIndex} className="space-y-2">
                        {items.map((item, iIndex) => {
                          const cleanItem = item.replace(/^\d+\.\s/, '').replace(/^-\s/, '');
                          return (
                            <div key={iIndex} className="flex gap-2 pl-1">
                              <span className="text-[#71717A] mt-0.5 shrink-0">{item.match(/^\d+\./) ? item.match(/^\d+\./)[0] : '•'}</span>
                              <span>{renderText(cleanItem)}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }

                  return <p key={pIndex}>{renderText(paragraph)}</p>;
                })}
              </div>
            </motion.section>
          ))}
        </div>

        {/* CTA Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-14 p-8 bg-[#1a1a1a] rounded-2xl text-center"
          data-testid="blog-post-cta"
        >
          <h3 className="text-xl font-bold text-white mb-3">Ready to Get Started?</h3>
          <p className="text-[#71717A] mb-6 max-w-lg mx-auto text-sm leading-relaxed">{post.cta}</p>
          <a
            href="https://calendly.com/agenticatoms/30min"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="blog-post-cta-button"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#050505] text-white rounded-full font-medium hover:bg-white/[0.05] transition-colors"
          >
            Book a Free Discovery Call <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>

        {/* Related Posts */}
        {related.length > 0 && (
          <div className="mt-16" data-testid="blog-related-posts">
            <h3 className="text-lg font-bold text-white mb-5">Related Articles</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map(r => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="group p-5 rounded-xl border border-white/[0.08] hover:border-white/10 hover:shadow-md transition-all"
                  data-testid={`related-post-${r.slug}`}
                >
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-2 ${
                    r.category === 'Industry' ? 'bg-[#FF6B00]/10 text-[#FF6B00]' : 'bg-[#FF6B00]/10 text-[#FF6B00]'
                  }`}>
                    {r.industry}
                  </span>
                  <h4 className="text-sm font-semibold text-white group-hover:text-[#A1A1AA] transition-colors leading-snug mb-1">
                    {r.title}
                  </h4>
                  <span className="text-xs text-[#71717A]">{r.readTime}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article></div>
    </div>
  );
};

export default BlogPostPage;
